import React from 'react';
import type {ReactNode} from 'react';
import * as Theme from '@sail/theme-classic';
import {useId} from '@sail/react-aria';

import {Description, ErrorText} from './FormField';
import type {FieldMetadata, SubsetOfKeys} from './FormField';
import {FormFieldGroupContext} from '../../components/FormFieldGroup';
import {view} from '../view';

export type FormFieldGroupProps = {
  /**
   * @external
   * The layout of the fields in the group.
   */
  layout?: 'row' | 'column';
  /**
   * @external
   * The text of the group's legend. This will be associated as a label with all fields inside the group.
   */
  legend?: ReactNode;
  /**
   * @external
   * Disables all fields in the group. Can be overridden on a per-field basis.
   */
  disabled?: boolean;
  /**
   * Descriptive text that will be rendered adjacent to the group's legend.
   * @external
   */
  description?: ReactNode;
  /**
   * Error to display above FormFieldGroup
   * @external
   */
  error?: React.ReactNode | void;
  /**
   * Sets the size prop on inner components where applicable
   * @external
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Used to position error message
   * Should not be exposed to users
   */
  isInCompositeField?: boolean;
};

export type FormFieldGroupElements = SubsetOfKeys<
  FormFieldGroupProps,
  'legend' | 'description'
>;

export type FormFieldGroupMetadata = FieldMetadata<FormFieldGroupElements>;

/**
 * @external
 * @param name
 * @param form
 */
export const FormFieldGroup = view.fieldset.create<FormFieldGroupProps>(
  (
    {
      layout = 'row',
      legend,
      disabled,
      error,
      description,
      children,
      isInCompositeField = false,
      size = 'medium',
      ...props
    },
    View,
  ) => {
    const legendId = useId();
    const descriptionId = useId();
    const errorId = useId();
    const ref = React.useRef<HTMLFieldSetElement>(null);
    const errorProps: {'aria-describedby'?: string; 'aria-invalid'?: boolean} =
      {};

    if (error) {
      errorProps['aria-describedby'] = errorId;
      errorProps['aria-invalid'] = true;
    }

    const handleClick = () => {
      ref.current
        ?.querySelector<HTMLElement>('select,button,input:not([disabled])')
        // eslint-disable-next-line @sail/ui-no-reflow
        ?.focus();
    };

    return (
      <FormFieldGroupContext.Provider
        value={{
          elements: {
            description: {
              id: descriptionId,
              present: Boolean(description),
            },
            legend: {
              id: legendId,
              present: Boolean(legend),
            },
          },
          disabled,
        }}
      >
        <View
          {...props}
          {...errorProps}
          uses={[
            Theme.FormFieldGroup,
            Theme.SelectVariants.internal_setAdaptiveDefaults({size}),
            Theme.TextAreaVariants.internal_setAdaptiveDefaults({size}),
            Theme.TextFieldVariants.internal_setAdaptiveDefaults({size}),
            Theme.CompositeFieldVariants.internal_setAdaptiveDefaults({
              size,
              disabled,
            }),
            {ref},
          ]}
          css={{stack: 'y', gap: 'xsmall'}}
        >
          <view.div>
            <view.legend
              onClick={handleClick}
              id={legendId}
              css={{
                font: 'bodyEmphasized',
                paddingX: 0,
              }}
            >
              {legend}
            </view.legend>
            {description ? (
              <Description id={descriptionId}>{description}</Description>
            ) : null}
            {!isInCompositeField ? (
              <div role="alert">
                {error ? (
                  <ErrorText role="none" id={errorId}>
                    {error}
                  </ErrorText>
                ) : null}
              </div>
            ) : null}
          </view.div>
          <view.div css={{layout}}>{children}</view.div>
          {isInCompositeField ? (
            <div role="alert">
              {error ? (
                <ErrorText role="none" id={errorId}>
                  {error}
                </ErrorText>
              ) : null}
            </div>
          ) : null}
        </View>
      </FormFieldGroupContext.Provider>
    );
  },
);
